import { xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { LinkClickedEvent } from '@atlassian/mpac-analytics';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BannerDetailsWidget, Button } from '@atlassian/mpac-primitives';
import { css, styled } from '@compiled/react';
import React from 'react';

import { ANALYTICS_SUBJECT_ID_BANNER_DETAILS_CTA } from '../../../../analytics/Events';
import { useResolvedCurrentUser } from '../../../../context/CurrentUserContext';
import useCTAClickHandler from '../../../../hooks/useCTAClickHandler';
import { MenuButtonMobile, SignIn } from '../Buttons';
import { AI_HUB_BANNER } from '../constants';
import { HelpDropdown, UserDropdown } from '../Dropdown';
import { AppsForProducts } from '../Menus/AppsForProducts';
import { Categories } from '../Menus/Categories';
import { RegulatedSectors } from '../Menus/RegulatedSectors';
import { Resources } from '../Menus/Resources';
import { expandableMenuLabel } from './constants';
import { DropdownMenuTransition, SlidingMenuTransition } from './Transitions';
import { ExpandableMenuType } from './types';
import { useRegulatedSectorFeatureFlags } from './useRegulatedSectorFeatureFlags';

import type { UserProps } from '../userTypes';
import type { ExpandableMenuProps } from './types';

import aiTeammate from '../../../../assets/images/aiTeammate.svg';

export const OpenedMenuContent = styled.div({
  width: '50%',
});

export const ClosedMenuContent = styled.div({
  width: '50%',
  display: 'flex',
  flexDirection: 'column',
});

const ExpandedMenuContainer = styled.div({
  overflowY: 'scroll',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

export const ActionsContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: 'auto',
  gap: token('space.100'),
  padding: `${token('space.100')} ${token('space.300')}`,
  [media.above.xs]: {
    padding: `${token('space.100')} ${token('space.400')}`,
  },
  [media.above.sm]: {
    padding: `${token('space.100')} ${token('space.500')}`,
  },
});

const FeedbackButton = styled(Button)({
  '&&': {
    width: 'fit-content !important',
    margin: `${token('space.300')}`,
  },
});

const bannerStyles = css({
  margin: `${token('space.400')} ${token('space.300')} ${token('space.100')} ${token('space.300')}`,
  backgroundColor: token('elevation.surface.sunken'),
  borderRadius: '1rem',
});

const borderStyles = xcss({
  borderTop: 'none',
});

export const Actions = ({
  userProps,
  openFeedbackDialog,
}: {
  userProps: UserProps;
  openFeedbackDialog: false | (() => void);
}) => {
  const { loggedInUser } = useResolvedCurrentUser({ userProps });

  return (
    <ActionsContainer>
      <HelpDropdown openFeedbackDialog={openFeedbackDialog} />
      {loggedInUser ? <UserDropdown userProps={userProps} /> : <SignIn userProps={userProps} />}
    </ActionsContainer>
  );
};

const ClosedMenu = ({
  openMenu,
  openFeedbackDialog,
  showRegulatedSectorsMenuButton,
  showAgentsButton,
  enableAppification,
}: {
  openMenu: (menuType: ExpandableMenuType) => () => void;
  closeMenu: () => void;
  openFeedbackDialog: false | (() => void);
  showRegulatedSectorsMenuButton: boolean;
  showAgentsButton: boolean;
  enableAppification: boolean;
}) => {
  const handleCtaClick = useCTAClickHandler();
  const appsForProductLabel = enableAppification
    ? 'Works with'
    : expandableMenuLabel[ExpandableMenuType.APPS_FOR_PRODUCTS];
  return (
    <ClosedMenuContent>
      <MenuButtonMobile
        onClick={openMenu(ExpandableMenuType.APPS_FOR_PRODUCTS)}
        label={appsForProductLabel}
      />
      <MenuButtonMobile
        onClick={openMenu(ExpandableMenuType.CATEGORIES)}
        label={expandableMenuLabel[ExpandableMenuType.CATEGORIES]}
      />
      <MenuButtonMobile
        onClick={openMenu(ExpandableMenuType.RESOURCES)}
        label={expandableMenuLabel[ExpandableMenuType.RESOURCES]}
      />
      {showRegulatedSectorsMenuButton && (
        <MenuButtonMobile
          onClick={openMenu(ExpandableMenuType.REGULATED_SECTORS)}
          label={expandableMenuLabel[ExpandableMenuType.REGULATED_SECTORS]}
        />
      )}
      {showAgentsButton && (
        <div css={bannerStyles}>
          <BannerDetailsWidget
            iconImage={aiTeammate}
            text={AI_HUB_BANNER.text}
            subText={AI_HUB_BANNER.subText}
            ctaText={AI_HUB_BANNER.ctaText}
            ctaHref={AI_HUB_BANNER.ctaHref}
            ctaType={AI_HUB_BANNER.ctaType}
            containerStyles={borderStyles}
            ctaAriaLabel={AI_HUB_BANNER.ctaAriaLabel}
            onCtaClick={(event) =>
              handleCtaClick(
                event,
                LinkClickedEvent,
                ANALYTICS_SUBJECT_ID_BANNER_DETAILS_CTA,
                {
                  destinationUrl: AI_HUB_BANNER.ctaHref,
                  linkText: AI_HUB_BANNER.ctaText,
                  dropdownName: 'mobile navigation menu',
                },
                AI_HUB_BANNER.ctaHref
              )
            }
          />
        </div>
      )}
      {openFeedbackDialog && (
        <FeedbackButton onClick={openFeedbackDialog}>Give Feedback</FeedbackButton>
      )}
    </ClosedMenuContent>
  );
};

const OpenedMenu = ({
  openedMenu,
  closeMenu,
  enableIsolatedCloudChanges,
  enableFedrampChanges,
}: {
  openedMenu?: ExpandableMenuType | null;
  closeMenu: () => void;
  enableIsolatedCloudChanges: boolean;
  enableFedrampChanges: boolean;
}) => {
  return (
    <OpenedMenuContent>
      {openedMenu === ExpandableMenuType.APPS_FOR_PRODUCTS && (
        <AppsForProducts closeMenu={closeMenu} />
      )}
      {openedMenu === ExpandableMenuType.CATEGORIES && <Categories closeMenu={closeMenu} />}
      {openedMenu === ExpandableMenuType.RESOURCES && <Resources closeMenu={closeMenu} />}
      {openedMenu === ExpandableMenuType.REGULATED_SECTORS && (
        <RegulatedSectors
          closeMenu={closeMenu}
          enableIsolatedCloudChanges={enableIsolatedCloudChanges}
          enableFedrampChanges={enableFedrampChanges}
        />
      )}
    </OpenedMenuContent>
  );
};

const ExpandedMenu = ({
  openedMenu,
  openMenu,
  closeMenu,
  userProps,
  openFeedbackDialog,
  enableAppification,
}: {
  openedMenu: ExpandableMenuType | null;
  openMenu: (menuType: ExpandableMenuType) => () => void;
  closeMenu: () => void;
  userProps: UserProps;
  openFeedbackDialog: false | (() => void);
  enableAppification: boolean;
}) => {
  const { showFedrampNavigationMenuButton, showIsolatedCloudNavigationMenuButton } =
    useRegulatedSectorFeatureFlags();
  const { enableMpacAgentsPageView } = useStatsig();
  const showAgentsButton = enableMpacAgentsPageView();

  return (
    <ExpandedMenuContainer>
      <Actions userProps={userProps} openFeedbackDialog={openFeedbackDialog} />
      <SlidingMenuTransition isOpen={openedMenu !== ExpandableMenuType.MOBILE_VIEW}>
        <ClosedMenu
          openMenu={openMenu}
          closeMenu={closeMenu}
          openFeedbackDialog={openFeedbackDialog}
          showRegulatedSectorsMenuButton={
            showFedrampNavigationMenuButton || showIsolatedCloudNavigationMenuButton
          }
          showAgentsButton={showAgentsButton}
          enableAppification={enableAppification}
        />
        <OpenedMenu
          openedMenu={openedMenu}
          closeMenu={closeMenu}
          enableIsolatedCloudChanges={showIsolatedCloudNavigationMenuButton}
          enableFedrampChanges={showFedrampNavigationMenuButton}
        />
      </SlidingMenuTransition>
    </ExpandedMenuContainer>
  );
};

export const ExpandableMenuMobile = ({
  isOpen,
  openedMenu,
  openMenu,
  closeMenu,
  userProps,
  openFeedbackDialog,
}: ExpandableMenuProps) => {
  const { enableAppificationChanges } = useStatsig();
  return (
    <DropdownMenuTransition isOpen={isOpen}>
      <ExpandedMenu
        openMenu={openMenu}
        openedMenu={openedMenu}
        closeMenu={closeMenu}
        userProps={userProps}
        openFeedbackDialog={openFeedbackDialog}
        enableAppification={enableAppificationChanges()}
      />
    </DropdownMenuTransition>
  );
};
